import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, provideAppInitializer, inject } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withNavigationErrorHandler,
} from '@angular/router';

import { ChunkLoadErrorHandler, reloadOnChunkLoadError } from '@/core/handlers/chunk-load-error.handler';

import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';

import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { ConfirmationService, MessageService } from 'primeng/api';

import { envelopeInterceptor } from '@/core/interceptors/envelope.interceptor';
import { errorInterceptor } from '@/core/interceptors/error.interceptor';
import { tokenInterceptor } from '@/core/interceptors/token.interceptor';

import { provideAngularSvgIcon } from 'angular-svg-icon';
import { PrimeNgCustomPreset } from './themes/primeng.theme';

import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

export function httpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, '/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled',
            }),
            withEnabledBlockingInitialNavigation(),
            withNavigationErrorHandler((error) => {
                if (reloadOnChunkLoadError(error.error)) {
                    return;
                }

                throw error;
            }),
        ),

        provideHttpClient(
            withInterceptors([
                tokenInterceptor,
                errorInterceptor,
                envelopeInterceptor,
            ]),
            withFetch(),
        ),

        provideAnimationsAsync(),
        provideAngularSvgIcon(),

        providePrimeNG({
            theme: {
                preset: PrimeNgCustomPreset,
                options: {
                    darkModeSelector: 'app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'theme, base, primeng',
                    },
                },
            },
        }),

        provideTranslateService({
            fallbackLang: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        }),

        // Sentry
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                logErrors: true,
            }),
        },

        {
            provide: Sentry.TraceService,
            deps: [Router],
        },

        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),

        MessageService,
        ConfirmationService,
    ],
};